/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import Joi from 'joi';
import { joiResolver } from '@hookform/resolvers/joi';
import { useForm } from 'react-hook-form';
import { ReCaptcha } from 'react-recaptcha-v3';
import { connect } from 'react-redux';
import styled, { css } from 'styled-components';
import { _ } from '_locales';

import { cts_secure } from '_cts';
import { colors } from '_styles';
import {
  WaitScreen,

} from '_components';
import apis, { siteKey, } from '_apis';
import {
  isRTL,
  eventTracker,
  createNewRecaptchaToken,
} from '_helpers';
import SingleCheckbox from '../../components/SingleCheckbox';

// style
const parentWidth = document.getElementById('rcroot').getBoundingClientRect().width;

const DownloadContainer = styled.div`
    max-width: 530px;
    margin: 0 auto;
    padding: 20px;
    box-sizing: border-box;
    direction: ${() => (isRTL() ? 'rtl' : 'ltr')};
`;

const EmailContainer = styled.div`
    position: relative;
    margin-bottom: 30px;
    display: ${({ checked }) => ((checked) ? 'block' : 'none')};
    margin-top: 15px;
`;

const EmailLabel = styled.label``;

const EmailSpan = styled.span`
    display: none;
`;

const Form = styled.form`
  margin-bottom: 50px;
  margin-top: 30px;
`;

const SubmitInput = styled.input`
  height: 39px;
  text-align: center;
  background-color: ${({ secondaryColor }) => secondaryColor};
  border: none;
  border-radius: 5px;
  padding: 0 15px;
  color: #ffffff;
  font-size: 14px !important;
  font-weight: 400 !important;
  display: block;
  margin: 15px auto;
  cursor: pointer;
`;

const Input = styled.input`
  width: calc(100% - 100px);
  height: 22px;
  border-radius: 6px;
  padding: 9px 12px;
  border: none;
  box-sizing: content-box;
  line-height: 0px;
  font-size: 14px !important;

  background-color: #f3f2f2;
  font-weight: 500 !important;
  margin: 0 auto;
  display: block;

  ${(parentWidth < 431) && css`
    display: block;
    box-sizing: border-box;
    padding: 20px 12px;
  `};
`;

const H3 = styled.h3`
  text-align: center;
  font-weight: 400 !important;
  font-size: 20px !important;
  margin-bottom: 40px;

  &.replacement-title {
    margin-top: 50px;
  }
`;

const ErrorText = styled.p`
  color: ${colors.RC_RED};
  margin-bottom: 25px;
  font-size: 13px;
  text-align: center;
`;

const CorrectText = styled.p`
  color: ${colors.RC_GREEN};
  margin-bottom: 25px;
  font-size: 13px;
  text-align: center;
`;

// End style

/**
 * This component is for preventing download file of schedule
 *
 * @author Alireza Zebardast
 * @param {object} plugin
 * @param {object} config
 * @param {object} zone
 */
const DownloadPDF = ({
  plugin, config, zone, appStage, fullAddress, mapCityName
}) => {
  const { projectId } = plugin;
  const { districtId, collection } = config;
  const { zoneStr } = zone;

  const [token, setToken] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState(false);
  const [checked, setChecked] = useState(true);
  const [loading, setLoading] = useState(false);

  const schema = Joi.object({
    email: (checked)
      ? Joi.string()
        .email({ tlds: false })
        .required()
        .messages({
          'string.empty': _('general.please_provide_your_email_address'),
          'string.email': _('general.invalid_email_address')
        })
      : Joi.string()
        .email({ tlds: false })
        .allow('').optional()
        .messages({
          'string.empty': _('general.please_provide_your_email_address'),
          'string.email': _('general.invalid_email_address')
        })
  });

  const { register, handleSubmit, errors } = useForm({ resolver: joiResolver(schema) });

  /* Error of validation */
  useEffect(() => {
    (async () => {
      try {
        if (
          Object.entries(errors).length !== 0
          && errors.constructor === Object
        ) {
          const token = await createNewRecaptchaToken(setToken);

          if (token !== 'done') throw new Error('There was a problem in generating new recaptcha token.');

          return Promise.resolve(token);
        }

        return undefined;
      } catch (e) {
        return Promise.reject(e);
      }
    })();
  }, [errors]);

  const handleCheckboxChange = () => setChecked(!checked);

  const verifyCallback = (generatedToken) => {
    setToken(generatedToken);
  };

  const onSubmit = async (result) => {
    setLoading(true);
    setError(false);
    setStatus('');

    const month = '6';

    const openPdfCalendar = () => {
      eventTracker('PDF Calendar Download', month);
      apis.openPdfCalendar(
        projectId,
        districtId,
        zoneStr,
        month,
        appStage,
      );
    };

    try {
      if (!checked) {
        openPdfCalendar();
        setLoading(false);
        return;
      }

      const payLoad = {
        email: result.email,
        project_id: projectId,
        district_id: districtId,
        zone_id: zoneStr,
        address: fullAddress || mapCityName.search_nm
      };

      const { data } = await cts_secure('post', apis.pdfSubscribe(), payLoad);
      const isSubscriptionFailed = !data || !data?.success || data?.success !== 'true';
      if (isSubscriptionFailed) {
        throw new Error(data?.message || 'Failed to subscribe for PDF calendar.');
      }

      setStatus(_('general.thank_you_for_subscribing'));
      openPdfCalendar();
    } catch (e) {
      setError(true);
      setStatus(_('general.sendData'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <ReCaptcha
        sitekey={siteKey}
        action="downloadPdf"
        verifyCallback={verifyCallback}
      />
      {
        loading
          ? (<WaitScreen />)
          : (
            <DownloadContainer>
              <H3 arial-label="download-pdf">
                {_('cal.download_your_6_months_pdf_calendar')}
              </H3>

              <Form action="submit" onSubmit={handleSubmit(onSubmit)} dir="ltr">
                <SingleCheckbox
                  id="checkbox-save-time"
                  name="checkbox"
                  value={checked}
                  checked={checked}
                  onClick={handleCheckboxChange}
                  label={_('cal.yes_sign_me_up')}
                />

                <EmailContainer checked={checked}>
                  <EmailLabel htmlFor="email">
                    <EmailSpan>{_('cal.email_image')}</EmailSpan>
                  </EmailLabel>
                  <Input
                    id="email"
                    type="email"
                    name="email"
                    ref={register}
                    placeholder={_('cal.your_email')}
                    required={checked}
                    aria-required="true"
                  />
                  {
                    (
                      errors
                      && Object.keys(errors).length
                      && errors.email
                    ) ? Object.keys(errors)
                      .filter((item) => item === 'email')
                      .map((item) => (<ErrorText role="alert" key={`${item}@${item.phone}`}>{errors.email.message}</ErrorText>)) : null
                  }

                </EmailContainer>
                <SubmitInput type="submit" value={_('cal.download_calendar')} secondaryColor={colors.RC_ACTION} />
                {status.length && !error ? (<CorrectText>{status}</CorrectText>) : null}
                {status.length && error ? (<ErrorText role="alert">{status}</ErrorText>) : null}
              </Form>
            </DownloadContainer>
          )
      }
    </div>
  );
};

const mapStateToProps = (store) => ({
  colors: store.config.templateColour ? JSON.parse(store.config.templateColour) : {},
  appStage: store.plugin.storeDistricts[store.config.districtId].stage || '',
  fullAddress: store.zone.fullAddress,
  mapCityName: store.zone.mapCityName,
});

export default connect(mapStateToProps)(DownloadPDF);
